import { getStpRatesRequest } from '@va/aaas/shared/actions';
import { selectStpRates } from '@va/aaas/shared/selectors';
import { getAiOnboardingCompleteFlag } from '@va/dashboard/selectors/core';
import { AiChatbotConversation, AiChatbotConversationQuota, AiModel } from '@va/dashboard/shared/ai-assistant';
import { useTooltipContext } from '@va/ui/tooltips';
import { is3asApp } from '@va/util/helpers';
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { KeyedMutator, SWRResponse } from 'swr';
import { useAvailableAiModels, useConversationQuota, useConversations } from '../api';
import { AiChatbotView } from '../types';

export type AiChatbotContextType = {
  selectedView: AiChatbotView;
  setSelectedView: (view: AiChatbotView) => void;
  onClose: () => void;
  aiModels: SWRResponse<AiModel[], Error>;
  isChatNameModalOpen: boolean;
  openEditChatNameModal: (chat: AiChatbotConversation, onSuccess?: () => void) => void;
  closeEditChatNameModal: () => void;
  selectedModel: string | null;
  setSelectedModel: (model: string) => void;
  selectedChat: Partial<AiChatbotConversation> | null;
  setSelectedChat: Dispatch<SetStateAction<Partial<AiChatbotConversation> | null>>;
  onEditSuccess: (() => void) | null;
  quota?: Pick<AiChatbotConversationQuota, 'limitReached' | 'questionsLimit'>;
  conversations?: AiChatbotConversation[];
  isConversationsLoading: boolean;
  mutateQuota: KeyedMutator<AiChatbotConversationQuota>;
  questionsCount: number;
};

const AiChatbotContext = createContext<AiChatbotContextType>({} as AiChatbotContextType);

type AiChatbotProviderProps = {
  questionsCount?: number;
};

export const AiChatbotProvider = ({
  children,
  questionsCount = 6,
}: React.PropsWithChildren<AiChatbotProviderProps>) => {
  const isAaasApp = is3asApp();
  const dispatch = useDispatch();
  const aiModels = useAvailableAiModels();
  const tooltipContext = useTooltipContext();

  const stpRates = useSelector(selectStpRates);

  const { data: conversationQuota, mutate: mutateConversationQuota } = useConversationQuota(!isAaasApp);

  const quota = useMemo(() => {
    if (isAaasApp && stpRates)
      return {
        limitReached: stpRates.stpLimit !== -1 && stpRates.stpConsumed >= stpRates.stpLimit,
        questionsLimit: -1,
      };

    return conversationQuota;
  }, [conversationQuota, isAaasApp, stpRates]);

  const mutateQuota = useCallback(() => {
    if (isAaasApp) dispatch(getStpRatesRequest());

    return mutateConversationQuota();
  }, [dispatch, isAaasApp, mutateConversationQuota]);

  const { data: conversations, isLoading: isConversationsLoading } = useConversations();

  const [isChatNameModalOpen, setIsChatNameModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState<Partial<AiChatbotConversation> | null>(null);

  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [onEditSuccess, setOnEditSuccess] = useState<(() => void) | null>(null);
  const isOnboardingCompleted = useSelector(getAiOnboardingCompleteFlag);

  const [selectedView, setSelectedView] = useState<AiChatbotView>(
    isOnboardingCompleted ? AiChatbotView.NewChat : AiChatbotView.Start,
  );

  useEffect(() => {
    if (!quota?.limitReached || selectedChat || isConversationsLoading) return;

    if (conversations?.length) {
      setSelectedChat(conversations[0]);
    } else {
      setSelectedView(AiChatbotView.NewChat);
    }
  }, [conversations, isConversationsLoading, quota?.limitReached, selectedChat]);

  useEffect(() => {
    if (!aiModels.data?.length) return;

    const firstConsentedModel = aiModels.data.find((m) => m.consented);

    setSelectedModel(firstConsentedModel?.name ?? null);
  }, [aiModels.data]);

  const openEditChatNameModal = useCallback(
    (chat: AiChatbotConversation, onSuccess?: () => void) => {
      setSelectedChat(chat);
      onSuccess && setOnEditSuccess(() => onSuccess);
      setIsChatNameModalOpen(true);
    },
    [setIsChatNameModalOpen],
  );

  const closeEditChatNameModal = useCallback(() => {
    setSelectedChat(null);
    setOnEditSuccess(null);
    setIsChatNameModalOpen(false);
  }, [setIsChatNameModalOpen]);

  const onClose = useCallback(() => {
    tooltipContext?.context?.onOpenChange(false);
  }, [tooltipContext]);

  return (
    <AiChatbotContext.Provider
      value={{
        selectedView,
        setSelectedView,
        onClose,
        aiModels,
        isChatNameModalOpen,
        openEditChatNameModal,
        closeEditChatNameModal,
        selectedChat,
        setSelectedChat,
        selectedModel,
        setSelectedModel,
        onEditSuccess,
        quota,
        conversations,
        isConversationsLoading,
        mutateQuota,
        questionsCount,
      }}
    >
      {children}
    </AiChatbotContext.Provider>
  );
};

export const useAiChatbotContext = () => useContext(AiChatbotContext);
